<script setup>
import { computed, provide } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { pageLoad } from '../../helpers/pageLoad';
import XlSlowNotice from '../xl/XlSlowNotice.vue';
import XlAvatar from '../xl/XlAvatar.vue';
import IconBack from '../icons/IconBack.vue';
import PolicyLineIcon from '../policyLines/PolicyLineIcon.vue';
import PortalFooter from './PortalFooter.vue';
import { useAuthStore } from '../../stores/auth';
import { useEventStore } from '../../stores/event';
import { isStaff } from '../../helpers/roles';
import { hubEntryPath } from '../../routes/guard';
import { usePortalBroker } from '../../composables/usePortalBroker';
import { usePortalUnread } from '../../composables/usePortalUnread';

/**
 * The Client Portal's one shell (XN-4552, XN-4553): the agency's logo, the
 * pages, the Messages badge and the policyholder's avatar, then the agency's
 * name in full above whichever page is open. One component, not a header per
 * page. My coverage is the agency's record of what they hold (XN-4556). My details is the
 * avatar, as in the prototype, not a nav item (Hunter, 2026-10-01).
 *
 * Recordings are past sessions, so they light Sessions. The icons are the
 * prototype's own (CNAV, build 12 Oct 17:52), by PolicyLineIcon name (XN-4736).
 */
const NAV = [
  { to: '/hub', label: 'Home', icon: 'home', exact: true },
  { to: '/hub/coverage', label: 'My coverage', icon: 'shield' },
  { to: '/hub/sessions', label: 'Sessions', icon: 'cal', also: ['/hub/recordings'] },
  { to: '/hub/library', label: 'Library', icon: 'book' },
  { to: '/hub/messages', label: 'Messages', icon: 'mail', badge: true },
];

// The top-level pages that start with a way Home. Messages has none; a page
// opened from one of them has its own way back (useBackTo), named for where
// the person came from.
const TOP_LEVEL = ['/hub/coverage', '/hub/sessions', '/hub/library', '/hub/details'];

const route = useRoute();
const auth = useAuthStore();
const eventStore = useEventStore();

const settings = computed(() => eventStore.event?.value ?? {});
const hubName = computed(() => settings.value.login_event_name || '');
const logo = computed(() => settings.value.login_logo || '');
// The footer's contact details. The keys can be missing (a payload cached
// before they existed) or null (not set); either reads as empty.
const contactEmail = computed(() => String(settings.value.login_event_contact_email ?? ''));
const contactPhone = computed(() => String(settings.value.login_event_support_phone ?? ''));
const user = computed(() => auth.userData ?? {});
const fullName = computed(() =>
  [user.value.first_name, user.value.last_name].filter(Boolean).join(' ').trim() || user.value.email || '');

const { total: unread, refresh } = usePortalUnread();
const { broker } = usePortalBroker();
provide('portalUnread', { refresh });

const badge = computed(() => {
  if (!unread.value) return '';
  return unread.value > 9 ? '9+' : String(unread.value);
});

const isOn = (item) => (item.exact
  ? route.path === item.to || route.path === `${item.to}/`
  : [item.to, ...(item.also ?? [])].some((path) => route.path.startsWith(path)));

const labelFor = (item) => (item.badge && badge.value ? `${item.label}, ${badge.value} unread` : item.label);

// Staff previewing the portal need a way back into their Hub.
const backTo = computed(() => (isStaff(auth.userRole) ? hubEntryPath(auth) : null));
</script>

<template>
  <div class="portal" data-xl-gen="northstar">
    <header class="portal-bar">
      <div class="portal-bar__in">
        <RouterLink to="/hub" class="portal-bar__brand" :title="hubName">
          <img v-if="logo" :src="logo" :alt="hubName" class="portal-bar__logo" />
          <b v-else class="portal-bar__name">{{ hubName }}</b>
        </RouterLink>

        <nav class="portal-nav" aria-label="Portal">
          <RouterLink
            v-for="item in NAV"
            :key="item.to"
            :to="item.to"
            class="portal-nav__item"
            :class="{ 'is-on': isOn(item) }"
            :aria-current="isOn(item) ? 'page' : undefined"
            :aria-label="labelFor(item)"
          >
            <PolicyLineIcon :name="item.icon" :size="18" />
            <span class="portal-nav__label">{{ item.label }}</span>
            <span v-if="item.badge && badge" class="portal-nav__badge" aria-hidden="true" data-testid="portal-unread">{{ badge }}</span>
          </RouterLink>
        </nav>

        <RouterLink v-if="backTo" :to="backTo" class="portal-bar__back" aria-label="Back to the Hub" data-testid="portal-back">
          <IconBack color="currentColor" width="16" height="16" />
          <span class="portal-bar__back-label">Back to the Hub</span>
        </RouterLink>

        <RouterLink to="/hub/details" class="portal-bar__avatar" aria-label="My details">
          <XlAvatar :name="fullName" :src="user.profile_image || ''" :color="user.avatar_color || ''" :size="38" />
        </RouterLink>
      </div>
    </header>

    <div class="portal-page">
      <p class="portal-page__agency" data-testid="portal-agency">{{ hubName }}</p>
      <RouterLink v-if="TOP_LEVEL.includes(route.path)" to="/hub" class="portal-page__home" data-testid="portal-home">
        <IconBack color="currentColor" width="15" height="15" /> Home
      </RouterLink>
      <!-- The page's area: it fades in as a page arrives; the agency's name,
           the bar and the footer are the shell and do not. -->
      <div data-page-slot>
        <XlSlowNotice />
        <RouterView v-slot="{ Component }">
          <component :is="Component" :key="pageLoad.retryKey" />
        </RouterView>
      </div>
    </div>
    <PortalFooter :name="hubName" :email="contactEmail" :phone="contactPhone" :logo="logo" :broker="broker" />
  </div>
</template>

<style scoped>
/* Measurements from the prototype's clientShell (build 29 Sep 15:06). */
.portal {
  /* A column the height of the window: the page grows to fill it, so the
     footer sits on the window's bottom edge on a short page and right after
     the content on a long one (Hunter, 2026-09-30). dvh, where supported,
     keeps that edge above a phone's browser toolbar. */
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--xl-color-surface);
  color: var(--xl-color-on-surface);
  font-family: var(--xl-font-family);
}

.portal-bar {
  position: sticky;
  top: 0;
  z-index: 40;
  background: #fff;
  border-bottom: 1px solid var(--xl-color-border);
}

.portal-bar__in {
  max-width: 940px;
  height: 64px;
  margin: 0 auto;
  padding: 0 26px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.portal-bar__brand {
  flex: 0 1 170px;
  display: flex;
  align-items: center;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

/* Height from the slot, width from the file: an SVG with no width or height
   attributes still renders, and nothing is stretched or cropped. */
.portal-bar__logo {
  display: block;
  height: 30px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.portal-bar__name {
  font-size: 15.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The bar is one line: labels go before anything wraps. */
.portal-nav {
  display: flex;
  gap: 3px;
  margin-left: auto;
  flex: 0 0 auto;
}

.portal-nav__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 15px;
  border-radius: 99px;
  font-size: 14px;
  font-weight: 500;
  color: var(--xl-color-on-surface-variant);
  text-decoration: none;
  white-space: nowrap;
  flex: 0 0 auto;
  transition: background 0.15s;
}

.portal-nav__item :deep(svg) {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
}

.portal-nav__item:hover,
.portal-nav__item.is-on {
  background: var(--xl-color-primary-container);
}

.portal-nav__item.is-on {
  color: var(--xl-color-primary);
  font-weight: 600;
}

.portal-nav__item:focus-visible,
.portal-bar__brand:focus-visible,
.portal-bar__back:focus-visible,
.portal-bar__avatar:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: 2px;
}

.portal-nav__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 99px;
  background: var(--xl-color-error);
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  display: grid;
  place-items: center;
  line-height: 1;
}

.portal-bar__back {
  font-size: 13px;
  font-weight: 500;
  color: var(--xl-color-primary);
  text-decoration: none;
  white-space: nowrap;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.portal-bar__back:hover { text-decoration: underline; }

.portal-bar__avatar {
  display: flex;
  color: inherit;
  text-decoration: none;
  border-radius: 50%;
}

.portal-page__home {
  display: inline-flex; align-items: center; gap: 8px; height: 34px; padding: 0 15px; margin-bottom: 18px;
  border-radius: 99px; border: 1px solid var(--xl-color-border); background: #fff; box-shadow: var(--xl-shadow-1);
  font-size: 13.5px; font-weight: 600; color: var(--xl-color-on-surface); text-decoration: none;
}
.portal-page__home:hover { border-color: var(--xl-color-primary-fixed-dim); background: var(--xl-color-primary-container); }
.portal-page {
  flex: 1 0 auto;
  width: 100%;
  box-sizing: border-box;
  max-width: 940px;
  margin: 0 auto;
  padding: 30px 26px 40px;
}

.portal-page__agency {
  font-size: 14px;
  font-weight: 600;
  color: var(--xl-color-on-surface-variant);
  margin: 0 0 16px;
  letter-spacing: -0.01em;
}

@media (max-width: 1040px) {
  .portal-nav__label { display: none; }
  .portal-nav__item { padding: 0 12px; }
  .portal-nav__badge { position: absolute; top: 2px; right: 2px; }
}

@media (max-width: 820px) {
  .portal-bar__name { display: none; }
}

@media (max-width: 560px) {
  .portal-bar__back-label { display: none; }
}

@media (max-width: 480px) {
  .portal-bar__in { padding: 0 16px; gap: 8px; }
  .portal-page { padding: 24px 16px 32px; }
}
</style>
